/**
 * CollectionOverviewPreviewPage.tsx — mock host page for VER-128 artboard 1a: the standalone
 * "Prepare — collection overview" visual refresh.
 *
 * PURPOSE:
 *   Design review only. Renders the shared CollectionOverviewPanel with the sticky select bar
 *   the canvas shows above it. It is deliberately NOT wired to a real collection id — the
 *   route param is accepted (to match the shape a real page would have) but ignored, and a
 *   banner says so, matching the ImpactScoresMockPage / SurveyResultsPage convention for
 *   preview-only pages elsewhere in this codebase.
 *
 * REMOVAL:
 *   Delete once the real ViewCollection screen adopts this visual language, or fold
 *   CollectionOverviewPanel into it directly.
 */
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { MOCK_COLLECTION } from "./createProgramme.mock";
import { CollectionOverviewPanel } from "./CollectionOverviewPanel";

export const CollectionOverviewPreviewPage: React.FC = () => {
    const { collectionId } = useParams<{ collectionId?: string }>();
    const [selected, setSelected] = useState(false);

    return (
        <Box display="flex" flexDirection="column" height="100%">
            <Box
                flex="0 0 auto"
                px={{ xs: 2, md: 4 }}
                py={2}
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    borderBottom: "1px solid",
                    borderColor: "borderShade.main",
                    bgcolor: "background.paper",
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Button variant={selected ? "outlined" : "contained"} onClick={() => setSelected((s) => !s)}>
                        {selected ? "Collection selected" : "Select this collection"}
                    </Button>
                    <Button variant="text">View another collection</Button>
                </Stack>
            </Box>

            <Box flex="1 1 auto" minHeight={0} overflow="auto" px={{ xs: 2, md: 4 }} py={3}>
                <Stack spacing={3} maxWidth="lg">
                    <Alert severity="info" variant="outlined">
                        Design preview (VER-128) — showing mock collection data
                        {collectionId ? ` in place of collection #${collectionId}` : ""}. Not connected to the real
                        story-collection API.
                    </Alert>

                    <Stack spacing={1.5}>
                        <Typography variant="body1" color="secondary.dark" sx={{ lineHeight: 1.6, maxWidth: "78ch" }}>
                            Here are some Story collections for your age range - review this collection and select or
                            view another collection
                        </Typography>
                        <Typography variant="h2" component="h1" color="secondary.main">
                            {MOCK_COLLECTION.name}
                        </Typography>
                    </Stack>

                    <CollectionOverviewPanel collection={MOCK_COLLECTION} />
                </Stack>
            </Box>
        </Box>
    );
};
